import { BusinessSegmentConditions } from '@reach/shared-types/conditions';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { appRoutes } from '../../../app-routes';
import {
  useAvailableFields,
  useSegmentDefinition,
  useUpdateSegment,
} from '../engage-segments-tab/useSegmentTemplates';
import styles from './EditSegment.module.css';
import { EditSegmentHeader } from './EditSegmentHeader';
import { RecipientsPreview } from './RecipientsPreview';
import { SegmentDefinitionEditor } from './SegmentDefinitionEditor';
import { SegmentNameDescription } from './SegmentNameDescription';

export const EditSegmentPage: React.FC = () => {
  const { canonicalVersionId } = useParams<{ canonicalVersionId: string }>();
  const navigate = useNavigate();

  const {
    data: segment,
    isLoading: isLoadingSegment,
    error: segmentError,
  } = useSegmentDefinition(canonicalVersionId);

  const {
    data: availableFields = [],
    isLoading: isLoadingFields,
    error: fieldsError,
  } = useAvailableFields();

  const { mutate: updateSegment, isPending: isUpdating } = useUpdateSegment(
    canonicalVersionId || '',
    {
      onSuccess: () => {
        navigate(appRoutes.ENGAGE_SEGMENTS);
      },
    },
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [contentsJson, setContentsJson] = useState('');
  const [isJsonValid, setIsJsonValid] = useState(true);

  // Initialize form when segment loads
  useEffect(() => {
    if (segment) {
      setName(segment.name);
      setDescription(segment.description || '');
      setContentsJson(JSON.stringify(segment.contents, null, 2));
    }
  }, [segment]);

  // Parse the current JSON to get conditions for preview
  // This must be before any early returns to follow React hooks rules
  const parsedConditions = useMemo((): BusinessSegmentConditions | null => {
    if (!isJsonValid || !contentsJson) return null;
    try {
      return JSON.parse(contentsJson) as BusinessSegmentConditions;
    } catch {
      return null;
    }
  }, [contentsJson, isJsonValid]);

  const handleSave = () => {
    if (!isJsonValid) return;

    const contents = JSON.parse(contentsJson);
    updateSegment({
      name,
      description,
      contents,
    });
  };

  const navigateToSegmentsList = () => {
    navigate(appRoutes.ENGAGE_SEGMENTS);
  };

  if (isLoadingSegment) {
    return <p className={styles.loadingState}>Loading segment...</p>;
  }

  if (segmentError || !segment) {
    return (
      <p className={styles.errorState}>
        Error loading segment. Please try again.
      </p>
    );
  }

  const isSaveDisabled =
    isUpdating ||
    isLoadingFields ||
    !name.trim() ||
    !description.trim() ||
    !isJsonValid;

  return (
    <article className={styles.page}>
      <EditSegmentHeader
        onBack={navigateToSegmentsList}
        onSave={handleSave}
        isSaveDisabled={isSaveDisabled}
        isSaving={isUpdating}
      />

      <main>
        <SegmentNameDescription
          name={name}
          description={description}
          onNameChange={setName}
          onDescriptionChange={setDescription}
        />

        <section className={styles.columns}>
          <SegmentDefinitionEditor
            contentsJson={contentsJson}
            onContentsChange={setContentsJson}
            onValidationChange={setIsJsonValid}
            availableFields={availableFields}
            isLoadingFields={isLoadingFields}
            fieldsError={fieldsError}
            isReadOnly={isUpdating}
          />

          <RecipientsPreview
            conditions={parsedConditions}
            isJsonValid={isJsonValid}
          />
        </section>
      </main>
    </article>
  );
};

export default EditSegmentPage;
